import { useBilling } from './BillingProvider';
import { getStripe } from './StripeProvider';
import type { PlanType } from './types';

/**
 * Hook to check if user can perform a specific action based on their plan
 */
export function useCanAccess() {
  const { capabilities } = useBilling();

  return {
    canEmbedNative: capabilities.canEmbedNative,
    canSaveProjects: capabilities.canSaveProjects,
    canAccess3DShaders: capabilities.canAccess3DShaders,
    hasCustomSupport: capabilities.hasCustomSupport,
    hasAdvancedExport: capabilities.hasAdvancedExport,
    canCreateProject: (currentProjectCount: number) =>
      currentProjectCount < capabilities.maxProjects,
  };
}

/**
 * Hook for subscription management
 */
export function useSubscription() {
  const {
    subscription,
    plan,
    isLoading,
    error,
    createCheckoutSession,
    createPortalSession,
    refreshSubscription,
  } = useBilling();

  const subscribe = async (priceId: string) => {
    try {
      const { url } = await createCheckoutSession(priceId);
      window.location.href = url;
    } catch (err) {
      console.error('Failed to start subscription:', err);
      throw err;
    }
  };

  const manageBilling = async () => {
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to open billing portal:', err);
      throw err;
    }
  };

  const isActive = subscription?.status === 'active';
  const isPastDue = subscription?.status === 'past_due';
  const isTrialing = subscription?.status === 'trialing';
  const willCancelAtPeriodEnd = subscription?.cancel_at_period_end ?? false;

  return {
    subscription,
    plan,
    isLoading,
    error,
    isActive,
    isPastDue,
    isTrialing,
    willCancelAtPeriodEnd,
    subscribe,
    manageBilling,
    refresh: refreshSubscription,
  };
}

/**
 * Hook for pricing information
 */
export function usePricing() {
  const { products, prices, isLoading } = useBilling();

  // Group prices by product
  const pricingPlans = products.map((product) => {
    const productPrices = prices.filter(
      (price) => price.product_id === product.id
    );
    return {
      ...product,
      prices: productPrices,
      monthlyPrice: productPrices.find((p) => p.interval === 'month'),
      yearlyPrice: productPrices.find((p) => p.interval === 'year'),
    };
  });

  return {
    products,
    prices,
    pricingPlans,
    isLoading,
  };
}

/**
 * Hook to check if user needs to upgrade for a specific feature
 */
export function useUpgradeCheck() {
  const { plan } = useBilling();
  const { subscribe } = useSubscription();
  const { prices } = usePricing();

  const needsUpgrade = (requiredPlan: PlanType) => {
    if (requiredPlan === 'free') return false;
    return plan === 'free' && requiredPlan === 'pro';
  };

  const upgradeToProLink = async () => {
    // Find the pro monthly price (you can customize this logic)
    const proMonthlyPrice = prices.find(
      (price) => price.interval === 'month' && price.metadata?.plan === 'pro'
    );

    if (!proMonthlyPrice) {
      throw new Error('Pro plan price not found');
    }

    return subscribe(proMonthlyPrice.id);
  };

  return {
    needsUpgrade,
    upgradeToProLink,
    currentPlan: plan,
  };
}

/**
 * Hook for Stripe checkout integration
 */
export function useStripeCheckout() {
  const { createCheckoutSession } = useBilling();

  const redirectToCheckout = async (priceId: string) => {
    try {
      const { sessionId } = await createCheckoutSession(priceId);
      const stripe = await getStripe();

      if (!stripe) {
        throw new Error('Stripe not loaded');
      }

      const { error } = await stripe.redirectToCheckout({ sessionId });

      if (error) {
        throw error;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      throw err;
    }
  };

  return { redirectToCheckout };
}
